// Shows AI trace, response versions, refine chat, and errors.

import { useEffect, useRef, useState } from 'react';
import {
  Briefcase,
  CaseSensitive,
  Check,
  ChevronRight,
  CornerDownLeft,
  Handshake,
  HandHeart,
  LoaderCircle,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useResponseSources } from '@/hooks/use-response-sources';
import { ErrorBar, TraceStepRow } from './composer-bars';
import { Astroid } from './icons';
import { ConfidencePill, ResponseBody, SourcePopover } from './response-card';
import { sourceAnchorFor, type SourceAnchor } from './source-anchor';
import { REFINE_STYLES, type GeneratedResponse, type RefineStyle, type TraceStep } from './types';

/** One-tap refine presets shown under the prompt input. */
const STYLE_ICONS: Record<RefineStyle, React.ComponentType<{ className?: string }>> = {
  shorten: Minimize2,
  expand: Maximize2,
  friendly: Handshake,
  formal: Briefcase,
  empathetic: HandHeart,
  'fix-grammar': CaseSensitive,
};

/** Right-aligned blue bubble echoing what the sentinel asked for. */
function RefineBubble({ label }: { label: string }) {
  return (
    <div className="flex justify-end">
      <span className="rounded-xl bg-blue-100 px-4 py-2.5 text-[13px] leading-[18px] text-text-heading">{label}</span>
    </div>
  );
}

/** "N STEPS PERFORMED" accordion above a response version. Only the newest starts open. */
function TraceSteps({ trace, isLatest }: { trace: TraceStep[]; isLatest: boolean }) {
  const [open, setOpen] = useState(isLatest);

  // Auto-minimize once a newer version supersedes this one.
  useEffect(() => {
    if (!isLatest) setOpen(false);
  }, [isLatest]);

  return (
    <div className="flex w-full flex-col gap-3">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex cursor-pointer items-center gap-1 transition-opacity hover:opacity-70"
      >
        <span className="font-mono text-[12px] font-medium uppercase leading-4 text-text-disabled">
          {trace.length} steps performed
        </span>
        <ChevronRight className={cn('size-4 text-gray-700 transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <div className="flex flex-col gap-2.5">
          {trace.map((step, index) => (
            <TraceStepRow key={step.id} step={step} showConnector={index < trace.length - 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function VersionCard({
  response,
  isLatest,
  onUse,
}: {
  response: GeneratedResponse;
  isLatest: boolean;
  onUse: () => void;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const sources = useResponseSources(response);
  const [openSource, setOpenSource] = useState<SourceAnchor | null>(null);
  return (
    <div
      ref={cardRef}
      className={cn(
        'relative flex w-full flex-col gap-4 rounded-[20px] border border-gray-500 bg-white/80 p-4 backdrop-blur-[6px]',
        !isLatest && 'opacity-80',
      )}
    >
      {openSource !== null && sources[openSource.index] && (
        <SourcePopover source={sources[openSource.index]} anchor={openSource} onClose={() => setOpenSource(null)} />
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {isLatest && <Astroid filled className="size-[13px] text-purple-500" />}
          <span className="font-mono text-[12px] font-medium uppercase leading-4 text-text-disabled">
            Generated Response
          </span>
        </div>
        <ConfidencePill pct={response.confidencePct} />
      </div>

      <ResponseBody
        markdown={response.markdown}
        onCitationClick={(index, chip) => setOpenSource(sourceAnchorFor(cardRef.current, chip, index))}
      />

      <div className="flex w-full justify-end">
        <Button onClick={onUse} className="px-3 text-[13px] font-normal">
          <Check className="size-[14px]" />
          Use Response
        </Button>
      </div>
    </div>
  );
}

interface AiChatOverlayProps {
  /** Refine mode only — omitted when the overlay is collecting a one-off instruction. */
  versions?: GeneratedResponse[];
  refineLabels?: Record<string, string>;
  pendingRefine?: { label: string; trace: TraceStep[]; stepsDone: number } | null;
  refineError?: string | null;
  refineLimitHit?: boolean;
  /** False while the run is still settling — the server refuses a refine until then. */
  refineReady?: boolean;
  onClose: () => void;
  onUse?: (responseId: string) => void;
  /** True while the overlay plays its exit, before the caller unmounts it. */
  closing?: boolean;
  onRefineStyle?: (style: RefineStyle, label: string) => void;
  onFreeRefine?: (input: { prompt: string }, label: string) => void;
  onRetryRefine?: () => void;
  /**
   * Collect a single instruction rather than refine a draft. The context stays on screen so the
   * agent can still read what they are turning down while they write.
   */
  instruction?: {
    placeholder: string;
    onSubmit: (text: string) => void;
    context?: React.ReactNode;
  };
}

export function AiChatOverlay({
  versions = [],
  refineLabels = {},
  pendingRefine = null,
  refineError = null,
  refineLimitHit = false,
  refineReady = true,
  onClose,
  onUse,
  closing,
  onRefineStyle,
  onFreeRefine,
  onRetryRefine,
  instruction,
}: AiChatOverlayProps) {
  const [prompt, setPrompt] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Freeze the composer while a refine runs, or while the server would refuse one.
  const composerDisabled = instruction ? false : refineLimitHit || !refineReady || pendingRefine !== null;

  // Jump to the newest exchange when a refine starts or lands, but never mid-run —
  // the object changes on every streamed step, which used to drag the agent back down.
  const refineRunning = pendingRefine !== null;
  useEffect(() => {
    // Scrolling a card that is already animating away reads as a flicker.
    if (closing) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [versions.length, refineRunning, refineError, closing]);

  const submitRefine = () => {
    const text = prompt.trim();
    if (!text) return;
    if (instruction) {
      instruction.onSubmit(text);
    } else {
      // Typed prompts read back as the agent wrote them; only the preset styles get the "Refine :" label.
      onFreeRefine?.({ prompt: text }, text);
    }
    setPrompt('');
  };

  return (
    <div
      className={cn(
        'absolute inset-0 z-30 flex flex-col px-3 pb-2.5 pt-3 duration-200',
        closing
          ? 'animate-out fade-out-0 zoom-out-95 slide-out-to-bottom-4'
          : 'animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4',
      )}
    >
      <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-3xl border border-gray-500 bg-gray-150/80 shadow-[0_3px_12px_0_rgba(0,0,0,0.16)] backdrop-blur-[6px]">
        {/* Frosted header floating over the chat — content blurs as it scrolls beneath. */}
        <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between rounded-t-3xl bg-gray-150/60 px-4 py-2.5 backdrop-blur-[6px]">
          <p className="text-[13px] font-medium leading-[18px] text-text-body">Kim AI</p>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            className="text-gray-900 hover:bg-white/60"
            aria-label="Minimize AI chat"
          >
            <Minimize2 className="size-[14px]" />
          </Button>
        </div>

        {/* Top/bottom padding clears the floating header and composer so cards scroll fully into view. */}
        <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6 pb-44 pt-14">
          {instruction?.context}
          {versions.map((version, index) => (
            <div key={version.id} className="flex w-full flex-col gap-4">
              {refineLabels[version.id] && <RefineBubble label={refineLabels[version.id]} />}
              {version.trace.length > 0 && <TraceSteps trace={version.trace} isLatest={index === versions.length - 1} />}
              <div className="pr-16">
                <VersionCard
                  response={version}
                  isLatest={index === versions.length - 1}
                  onUse={() => onUse?.(version.id)}
                />
              </div>
            </div>
          ))}

          {pendingRefine && (
            <>
              <RefineBubble label={pendingRefine.label} />
              <div className="flex items-center gap-1">
                <LoaderCircle className="size-[14px] animate-spin text-gray-700" />
                <span className="font-mono text-[12px] font-medium uppercase leading-4 text-text-disabled">
                  AI thinking
                </span>
                {/* Steps stream in expanded below, so the chevron shows the open state. */}
                <ChevronRight className="size-4 rotate-90 text-gray-700" />
              </div>
              {/* Trace streams in one step at a time while the refine runs. */}
              {pendingRefine.stepsDone > 0 && (
                <div className="flex flex-col gap-2.5">
                  {pendingRefine.trace.slice(0, pendingRefine.stepsDone).map((step, index) => (
                    <TraceStepRow
                      key={step.id}
                      step={step}
                      showConnector={index < pendingRefine.stepsDone - 1}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {refineError && (
            <ErrorBar message={refineError} onRetry={onRetryRefine ?? (() => {})} />
          )}
        </div>

        {/* Floats over the chat so content fades out beneath it instead of hard-clipping. */}
        <div className="absolute inset-x-0 bottom-0 rounded-b-3xl bg-gradient-to-b from-transparent via-gray-150/80 to-gray-150 p-4 pt-8">
          <div className="flex w-full flex-col gap-5 rounded-2xl border border-gray-500 bg-white/80 px-4 pb-2.5 pt-5 shadow-[0_-3px_12px_0_rgba(0,0,0,0.08)] backdrop-blur-[6px]">
            <input
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submitRefine();
              }}
              placeholder={instruction?.placeholder ?? 'Prompt what you would like to draft...'}
              className="w-full bg-transparent text-[13px] leading-[18px] text-text-body outline-none placeholder:text-gray-800"
              disabled={composerDisabled}
            />
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {!instruction && REFINE_STYLES.map(({ value, label }) => {
                  const Icon = STYLE_ICONS[value];
                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={composerDisabled}
                      onClick={() => onRefineStyle?.(value, label)}
                      className="flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-500 bg-white px-2.5 py-1 text-[12px] leading-4 text-text-emphasis transition-colors hover:bg-gray-100 disabled:cursor-default disabled:opacity-50"
                    >
                      <Icon className="size-3.5 text-text-emphasis" />
                      {label}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={submitRefine}
                className="shrink-0 text-gray-900"
                aria-label="Send refine prompt"
                disabled={composerDisabled}
              >
                <CornerDownLeft className="size-[14px]" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
